import { Loader2, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePipelineRunReport, useResendReport } from '@/hooks/usePipelineRuns';
import { extractErrorMessage } from '@/lib/utils';

export const ReportTab = ({ runId }: { runId: string }) => {
  const { toast } = useToast();
  const { data: report, isLoading } = usePipelineRunReport(runId);
  const resend = useResendReport();

  const handleResend = async () => {
    try {
      await resend.mutateAsync(runId);
      toast({ title: 'Report re-sent' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Resend failed',
        description: extractErrorMessage(err),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Report</CardTitle>
          <div className="flex items-center gap-2">
            {report?.report_delivered_at && (
              <span className="text-muted-foreground text-xs">
                Delivered {new Date(report.report_delivered_at).toLocaleString()}
              </span>
            )}
            {!report?.report_delivered_at && report?.report_error && (
              <span className="text-destructive text-xs" title={report.report_error}>
                Delivery failed: {report.report_error}
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={resend.isPending || !report?.report_html}
              onClick={handleResend}
            >
              {resend.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Send className="mr-1 h-3 w-3" />
              )}
              Re-send
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {report?.report_html ? (
          // HTML is server-generated and escaped by report-builder.ts
          <div
            className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: report.report_html }}
          />
        ) : (
          <p className="text-muted-foreground text-sm">No report generated yet.</p>
        )}
      </CardContent>
    </Card>
  );
};
